// "pdfjs-dist": "3.11.174",
import * as pdfjsLib from 'pdfjs-dist'
import JsZip from 'jszip'
import { downloadBlob } from './utils'
import { DEFAULT_IMAGE_FORMAT } from './index'

const PDF_WORKER_SRC = '/pdfjs-dist/pdf.worker.js'

// 结构优化
// PDF解析类
export class PDFParser {
  constructor(workerSrc = PDF_WORKER_SRC) {
    this.workerSrc = PDF_WORKER_SRC
  }
  async init() {
    if (!pdfjsLib || pdfjsLib.GlobalWorkerOptions.workerSrc === PDF_WORKER_SRC) return
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC
  }
  clear() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = ''
  }
  async parse(file, abortSignal) {
    if (!file) throw new Error('[PDFParser: parse]请选择文件')
    const arrayBuffer = await file.arrayBuffer()
    if (abortSignal && abortSignal.aborted) {
      throw new Error('[PDFParser: parse]PDF parsing aborted')
    }
    return await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  }
}

// 工具类-PDF页面渲染
export class PDFPageRenderer {
  constructor({ format = DEFAULT_IMAGE_FORMAT, quality = 1.0, scale = 1.0 } = {}) {
    this.format = format
    this.quality = quality
    this.scale = scale
  }
  setScale(scale = this.scale) {
    this.scale = scale
  }
  setFormat(format = this.format) {
    this.format = format
  }
  setQuality(quality = this.quality) {
    this.quality = quality
  }
  prepareCanvas(page, { scale = this.scale } = {}) {
    if (!page) throw new Error('[prepareCanvas Error]请传入有效的PDF页面对象')
    const viewport = page.getViewport({ scale })
    // 创建 Canvas
    const canvas = document.createElement('canvas')
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`
    canvas.width = viewport.width
    canvas.height = viewport.height
    return canvas
  }
  async render({ page, canvas, scale = this.scale } = {}) {
    if (!page) throw new Error('[render Error: Page]请传入有效的PDF页面对象')
    if (!canvas) throw new Error('[render Error: Canvas]请传入有效的Canvas对象')

    const context = canvas.getContext('2d')
    // 渲染到 Canvas
    return await page.render({
      intent: 'print', // 关键配置 - 使用打印质量
      canvasContext: context,
      viewport: page.getViewport({ scale })
    }).promise
  }

  async getCanvasImage({ canvas, signal } = {}) {
    if (!canvas) throw new Error('[getCanvasImage Error: Canvas]请传入有效的Canvas对象')
    sharpenCanvas(canvas)
    const { format = DEFAULT_IMAGE_FORMAT, quality = 1.0 } = this
    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (signal && signal.aborted) {
              reject(new Error('[getCanvasImage Error]PDF parsing aborted'))
              return
            }
            if (!blob) {
              reject(new Error(`[getCanvasImage Error: Blob]无图片数据`))
            } else {
              resolve(blob)
            }
          },
          `image/${format}`,
          quality
        )
      })
      return blob
    } finally {
      canvas.remove()
    }
  }
}

// 工具类-bit图片提取
export class BitImageProgressor {
  constructor({ format = DEFAULT_IMAGE_FORMAT, quality = 1.0 } = {}) {
    this.format = format
    this.quality = quality
  }
  setFormat(format = this.format) {
    this.format = format
  }
  setQuality(quality = this.quality) {
    this.quality = quality
  }
  async convert(bitmap) {
    // 创建 OffscreenCanvas（兼容主线程和 Web Worker）
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
    const ctx = canvas.getContext('bitmaprenderer')

    // 将 ImageBitmap 渲染到 Canvas
    ctx.transferFromImageBitmap(bitmap)

    // 转换为 Blob（支持 MIME 类型动态设置）
    const { format, quality } = this
    const mimeType = `image/${format}`
    return canvas.convertToBlob({ type: mimeType, quality })
  }
}

// 工具类-压缩文件处理类
export class ZipProgressor {
  constructor() {
    this.zip = null
    this.zipName = ''
  }
  init(zipName) {
    this.zip = new JsZip()
    this.zipName = zipName
  }
  async addFile(filename, data, options = {}) {
    if (!this.zip) {
      throw new Error('[ZipProgressor: addFile]请先调用 init 方法初始化 Zip 对象')
    }
    this.zip.file(filename, data, options)
  }
  generateAsync({ zipName = this.zipName, clear = false } = {}) {
    if (!this.zip) {
      throw new Error('[ZipProgressor: generateAsync]请先调用 init 方法初始化 Zip 对象')
    }
    this.zip.generateAsync({ type: 'blob' }).then((content) => {
      downloadBlob(content, { options: { type: 'application/zip' }, name: zipName })
      if (clear) {
        this.clear()
      }
    })
  }
  clear() {
    this.zip = null
  }
}
// 工具类 - 事件触发器
export class PDFEventEmitter {
  constructor() {
    this.events = new Map()
  }

  on(event, listener, { signal } = {}) {
    if (!listener || typeof listener !== 'function') {
      throw new Error('[PDFEventEmitter: on]listener must be a function')
    }
    if (!this.events.has(event)) {
      this.events.set(event, new Set())
    }
    const listenerList = this.events.get(event)
    listenerList.add(listener)
    if (signal && !signal.aborted) {
      const removeHandler = () => this.off(event, listener)
      signal.addEventListener('abort', removeHandler, { once: true })
    }
  }
  off(event, listener) {
    if (!this.events.has(event)) return
    const listenerSet = this.events.get(event)
    if (listener) {
      listenerSet.delete(listener)
    } else {
      listenerSet.clear()
    }
    if (listenerSet.size === 0) {
      this.events.delete(event)
    }
  }
  emit(event, ...args) {
    if (!this.events.has(event)) return
    const listeners = [...this.events.get(event)]
    for (const listener of listeners) {
      try {
        listener(...args)
      } catch (err) {
        console.error(`[${event}]事件处理出错：`, err)
      }
    }
  }
  clearListener() {
    this.events.clear()
  }
}

// 使用卷积核进行锐化
export function sharpenCanvas(canvas) {
  const ctx = canvas.getContext('2d')
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // 应用Unsharp Masking算法
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]

    // 相邻像素差异计算
    const diffR = r - (data[i - 4] || r)
    const diffG = g - (data[i - 3] || g)
    const diffB = b - (data[i - 2] || b)

    // 锐化增强
    data[i] = Math.min(255, r + diffR * 0.7)
    data[i + 1] = Math.min(255, g + diffG * 0.7)
    data[i + 2] = Math.min(255, b + diffB * 0.7)
  }

  ctx.putImageData(imageData, 0, 0)
}
